import { WebMercatorViewport } from "@deck.gl/core"
import { getLineSegmentBoxIntersections } from "@nova/calculate-geospatial"
import { GeometryType, LineStroke, LineWidth } from "@nova/constants"
import { Coordinate, Line } from "@nova/protobuf"
import { getViewport } from "features/map/GlobalViewState"

export const convertLineStrokeToValue = (strokeName?: LineStroke) => {
    if (strokeName === LineStroke.dashed) {
        return [10, 10]
    }
    if (strokeName === LineStroke.dotted) {
        return [0.25, 4]
    }
    if (strokeName === LineStroke.squared) {
        return [2, 2]
    }
    return [0, 0]
}

export const convertLineWidthToValue = (properties?: {
    width?: LineWidth
    stroke?: LineStroke
}) => {
    if (!properties) return 2
    const { width, stroke } = properties
    if (width === LineWidth.thick) {
        if (stroke === LineStroke.dashed) {
            return 4
        }
        if (stroke === LineStroke.dotted) {
            return 8
        }
        if (stroke === LineStroke.squared) {
            return 8
        }
        return 4
    }
    if (stroke === LineStroke.dashed) {
        return 2
    }
    if (stroke === LineStroke.dotted) {
        return 4
    }
    if (stroke === LineStroke.squared) {
        return 4
    }
    return 2
}

/*** Geometry Icon Layer Helpers ***/

/**
 * The two functions below are used to make sure the line icons for the GeometryIconLayer are angled the correct direction
 *
 * */
function calculateDiagonalOffset(angle: number) {
    // Define the maximum offset
    const maxOffset = 20 // Adjust this value as needed
    const negativeOffset = angle > 90

    // Calculate the deviation from horizontal and vertical
    const nearestMultipleOf90 = Math.round(angle / 90) * 90
    const diagonalDeviation = Math.abs(angle - nearestMultipleOf90)

    // The closer the angle is to 45°, the larger the offset
    const offsetFactor = 55
    const diagonalFactor = Math.min(diagonalDeviation, 90) / offsetFactor

    // Calculate the offset based on the diagonal factor
    const offset = diagonalFactor * maxOffset
    return negativeOffset ? offset * -1 : offset
}

export function calculateBearings(point1: Coordinate, point2: Coordinate) {
    const { longitude: x1, latitude: y1 } = point1
    const { longitude: x2, latitude: y2 } = point2
    const dx = x2 - x1
    const dy = y2 - y1
    let angle = (Math.atan2(dy, dx) * 180) / Math.PI // Convert radians to degrees

    if (angle < 0) {
        angle += 180 // Normalize to 0-360 degrees
    }
    // Calculate offset based on how diagonal the angle is
    const offset = calculateDiagonalOffset(angle)
    angle += offset
    if (angle > 90) {
        angle += 180
    }
    return angle
}

/**
 * Interpolates additional points between a pair of longitude and latitude points based on segment length.
 * Clamps the line segment to only be the portion that crosses the users screen,
 * then finds the lines pixel distance and determines exactly how many icons can fit on that line
 *
 */
function interpolatePointsSmart(
    point1: Coordinate,
    point2: Coordinate,
    viewport: WebMercatorViewport,
    iconWidth: number
) {
    const interpolatedPoints: { geometry: { coordinates: Coordinate }; bearing: number }[] = []
    const bearing = calculateBearings(point1, point2)

    const [viewportMinLng, viewportMaxLat] = viewport.unproject([0, 0])
    const [viewportMaxLng, viewportMinLat] = viewport.unproject([viewport.width, viewport.height])

    const intersections = getLineSegmentBoxIntersections(point1, point2, {
        minx: viewportMinLng,
        miny: viewportMinLat,
        maxx: viewportMaxLng,
        maxy: viewportMaxLat,
    })
    if (intersections.length < 2) return []

    const [startIntersect, endIntersect] = intersections
    const [x1, y1] = viewport.project(startIntersect)
    const [x2, y2] = viewport.project(endIntersect)

    const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
    const numberOfElements = Math.floor(distance / iconWidth)

    // Shift the first point inwards so that it aligns with the endpoints
    const firstLng =
        startIntersect[0] + (0.5 * (endIntersect[0] - startIntersect[0])) / (numberOfElements + 1)
    const firstLat =
        startIntersect[1] + (0.5 * (endIntersect[1] - startIntersect[1])) / (numberOfElements + 1)
    interpolatedPoints.push({
        geometry: { coordinates: { longitude: firstLng, latitude: firstLat } },
        bearing,
    })

    //interpolate the middle points
    for (let i = 1; i <= numberOfElements; i++) {
        const lng =
            startIntersect[0] + (i * (endIntersect[0] - startIntersect[0])) / (numberOfElements + 1)
        const lat =
            startIntersect[1] + (i * (endIntersect[1] - startIntersect[1])) / (numberOfElements + 1)

        interpolatedPoints.push({
            geometry: { coordinates: { longitude: lng, latitude: lat } },
            bearing,
        })
    }

    // Same as the first point, but for the last point
    const lastLng =
        endIntersect[0] - (0.5 * (endIntersect[0] - startIntersect[0])) / (numberOfElements + 1)
    const lastLat =
        endIntersect[1] - (0.5 * (endIntersect[1] - startIntersect[1])) / (numberOfElements + 1)
    interpolatedPoints.push({
        geometry: { coordinates: { longitude: lastLng, latitude: lastLat } },
        bearing,
    })

    return interpolatedPoints
}

/**
 *
 * Used for our Line/Polygon icon packs to convert line segments into individual points where icons are placed
 *
 * @param geometries the lines/polygons that are converted into icon points
 * @param viewport the viewport, used to project points to the screen and clamp lines
 * @param iconWidth the width of the icon, right now all of our icons for this are 33px wide
 * @returns a list of the icons Coordinate Point, its Url String, and its Bearing Number
 */
export const generateIconGeometry = (geometries: Line[], iconWidth = 33) => {
    const iconData: { point: Coordinate; iconUrl: string; bearing: number }[] = []
    for (const geometry of geometries) {
        if (geometry.geometryType !== GeometryType.Line || !geometry.url) continue

        const path = geometry.coordinates
        if (path.length > 0) {
            const url = geometry.url as string
            const [pack, iconKey] = url.split("/")
            const key = iconKey
                .split("_")
                .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
                .join("_")

            const iconUrl = `/icons/${pack}/svg/${key}.svg`
            for (let i = 0; i < path.length - 1; i++) {
                const p1 = path[i]
                const p2 = path[i + 1]

                const points = interpolatePointsSmart(p1, p2, getViewport(), iconWidth)
                points.forEach((point) => {
                    iconData.push({
                        point: point.geometry.coordinates,
                        iconUrl,
                        bearing: point.bearing,
                    })
                })
            }
        }
    }
    return iconData
}
